// Reads back the JQL the builder writes (jql.ts): clauses joined by AND, each `field = v`,
// `field != v`, `field in (v, …)`, `assignee is EMPTY` or a function value, then an optional
// `ORDER BY field DIR`. Anything else (OR, NOT, parentheses around clauses, other fields)
// is null: the text stays raw JQL.

type Token = { kind: "word" | "string" | "func"; text: string } | { kind: "punct"; text: "(" | ")" | "," | "=" | "!=" };

export interface Clause {
  /** Lower-cased; `type` is read as `issuetype`. */
  field: string;
  op: "=" | "!=" | "in" | "is";
  values: string[];
}

const FIELDS = new Set(["project", "issuetype", "status", "statuscategory", "assignee", "labels", "sprint"]);

function tokenize(text: string): Token[] | null {
  const out: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const c = text[i]!;
    if (/\s/.test(c)) {
      i++;
    } else if (c === '"' || c === "'") {
      let value = "";
      i++;
      while (i < text.length && text[i] !== c) {
        if (text[i] === "\\" && i + 1 < text.length) i++;
        value += text[i];
        i++;
      }
      if (i >= text.length) return null;
      i++;
      out.push({ kind: "string", text: value });
    } else if (c === "!" && text[i + 1] === "=") {
      out.push({ kind: "punct", text: "!=" });
      i += 2;
    } else if (c === "(" || c === ")" || c === "," || c === "=") {
      out.push({ kind: "punct", text: c });
      i++;
    } else {
      const m = /^[^\s(),="'!]+/.exec(text.slice(i));
      if (!m) return null;
      i += m[0].length;
      if (text.slice(i, i + 2) === "()") {
        out.push({ kind: "func", text: `${m[0]}()` });
        i += 2;
      } else {
        out.push({ kind: "word", text: m[0] });
      }
    }
  }
  return out;
}

const isWord = (t: Token | undefined, word: string) => t?.kind === "word" && t.text.toLowerCase() === word;
const isValue = (t: Token | undefined): t is Token & { kind: "word" | "string" } => t?.kind === "word" || t?.kind === "string";

function clause(tokens: Token[]): Clause | null {
  const [head, op, ...rest] = tokens;
  if (head?.kind !== "word" || !op) return null;
  const field = head.text.toLowerCase() === "type" ? "issuetype" : head.text.toLowerCase();
  if (!FIELDS.has(field)) return null;
  const funcOnly = (name: string) => rest.length === 1 && rest[0]!.kind === "func" && rest[0]!.text.toLowerCase() === name.toLowerCase();
  if (field === "assignee") {
    if (op.kind === "punct" && op.text === "=" && funcOnly("currentUser()")) return { field, op: "=", values: [] };
    if (isWord(op, "is") && rest.length === 1 && isWord(rest[0], "empty")) return { field, op: "is", values: [] };
    return null;
  }
  if (field === "sprint") return isWord(op, "in") && funcOnly("openSprints()") ? { field, op: "in", values: [] } : null;
  if (op.kind === "punct" && (op.text === "=" || op.text === "!=")) {
    const [value] = rest;
    if (rest.length !== 1 || !isValue(value)) return null;
    if (op.text === "!=" && field !== "statuscategory") return null;
    return { field, op: op.text, values: [value.text] };
  }
  if (!isWord(op, "in") || field === "statuscategory") return null;
  const values = list(rest);
  return values && values.length > 0 ? { field, op: "in", values } : null;
}

/** `( v , v , … )` → the values. */
function list(tokens: Token[]): string[] | null {
  if (tokens[0]?.text !== "(" || tokens[tokens.length - 1]?.text !== ")") return null;
  const inner = tokens.slice(1, -1);
  const values: string[] = [];
  for (let i = 0; i < inner.length; i += 2) {
    const v = inner[i];
    if (!isValue(v)) return null;
    values.push(v.text);
    if (i + 1 < inner.length && inner[i + 1]?.text !== ",") return null;
  }
  return inner.length % 2 === 1 ? values : null;
}

/** The clauses and the `ORDER BY` term (`"priority DESC"`), or null when it is not builder JQL. */
export function parseClauses(text: string): { clauses: Clause[]; order: string | null } | null {
  const tokens = tokenize(text);
  if (!tokens) return null;
  const at = tokens.findIndex((t, i) => isWord(t, "order") && isWord(tokens[i + 1], "by"));
  const where = at < 0 ? tokens : tokens.slice(0, at);
  let order: string | null = null;
  if (at >= 0) {
    const [field, dir, ...extra] = tokens.slice(at + 2);
    if (field?.kind !== "word" || extra.length > 0 || (dir && !(isWord(dir, "asc") || isWord(dir, "desc")))) return null;
    order = dir ? `${field.text} ${dir.text}` : field.text;
  }
  const clauses: Clause[] = [];
  let current: Token[] = [];
  for (const t of [...where, null]) {
    if (t === null || isWord(t, "and")) {
      if (current.length === 0) {
        if (t === null && clauses.length === 0) break;
        return null;
      }
      const c = clause(current);
      if (!c) return null;
      clauses.push(c);
      current = [];
    } else {
      current.push(t);
    }
  }
  return { clauses, order };
}
